import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import SupportChoiceBar from "../components/SupportChoiceBar";
import useGuidance from "../hooks/useGuidance";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Users, MapPin, Shield,
  User, Mail, Phone, Briefcase, Star, Plus, Loader2,
  X, ClipboardCheck, Activity, Globe, RefreshCw,
  Bell, Server, Lock, Database, AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";
import {
  ORG_STATUS_STYLES as STATUS_STYLES,
  RISK_STYLES,
  ACTIVATION_STATUS_STYLES,
  OBLIGATION_CATEGORY_CONFIG,
  REGION_LABELS,
} from "../constants/registry";

// Icons are a UI concern — compose with shared registry data
const CATEGORY_ICONS = {
  incident_notification: Bell,
  data_residency: Server,
  privacy_security: Lock,
  record_integrity: Database,
  breach_reporting: AlertCircle,
};
const CATEGORY_CONFIG = Object.fromEntries(
  Object.entries(OBLIGATION_CATEGORY_CONFIG).map(([k, v]) => [k, { ...v, icon: CATEGORY_ICONS[k] }])
);

// ============== SUB-COMPONENTS ==============

const ObligationRow = ({ activation }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation('platform');
  const catCfg = CATEGORY_CONFIG[activation.category] || CATEGORY_CONFIG.incident_notification;
  const CatIcon = catCfg.icon;
  const statusStyle = ACTIVATION_STATUS_STYLES[activation.applicability_status] || ACTIVATION_STATUS_STYLES.active;

  return (
    <div
      className="p-3 border-b border-[rgba(255,255,255,0.04)] last:border-0"
      data-testid={`obligation-row-${activation.activation_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${catCfg.bg} shrink-0 mt-0.5`}>
          <CatIcon className={catCfg.color} size={12} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-white text-xs font-medium">{activation.title}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
            <span className="text-[#36A9E1] text-[10px] font-mono">{activation.region}</span>
          </div>
          <p className="text-[#8B95A5] text-[11px] leading-relaxed">{activation.reference_label}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-[#36A9E1] text-[10px] mt-1 hover:text-[#38BDF8] transition-colors"
            data-testid={`obligation-expand-${activation.activation_id}`}
          >
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            {expanded ? t('crm.hideDetails') : t('crm.showDetails')}
          </button>
          {expanded && (
            <div className="mt-2 space-y-2 text-[11px]">
              <p className="text-[#B8C7D9] leading-relaxed">{activation.description}</p>
              {activation.data_residency_notes && (
                <div className="bg-[rgba(255,255,255,0.02)] rounded p-2">
                  <span className="text-[#8B95A5] font-medium block mb-0.5">Data Residency Notes:</span>
                  <span className="text-[#B8C7D9]">{activation.data_residency_notes}</span>
                </div>
              )}
              {activation.notification_notes && (
                <div className="bg-[rgba(255,255,255,0.02)] rounded p-2">
                  <span className="text-[#8B95A5] font-medium block mb-0.5">Notification Notes:</span>
                  <span className="text-[#B8C7D9]">{activation.notification_notes}</span>
                </div>
              )}
              <div className="text-[10px] text-[#8B95A5] italic">{activation.activation_reason}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ObligationsSummaryCard = ({ summary }) => {
  const { t } = useTranslation('platform');
  if (!summary) return null;
  const { total_obligations, by_status, high_priority_count, regions_count } = summary;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="crm-obligations-summary">
      <div className="p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
        <div className="text-[#8B95A5] text-[10px] mb-1">{t('crm.regions')}</div>
        <div className="text-white text-lg font-bold font-mono">{regions_count}</div>
      </div>
      <div className="p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
        <div className="text-[#8B95A5] text-[10px] mb-1">{t('crm.activeObligations')}</div>
        <div className="text-emerald-400 text-lg font-bold font-mono">{by_status?.active || 0}</div>
      </div>
      <div className="p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
        <div className="text-[#8B95A5] text-[10px] mb-1">{t('crm.underReview')}</div>
        <div className="text-amber-400 text-lg font-bold font-mono">{by_status?.under_review || 0}</div>
      </div>
      <div className="p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
        <div className="text-[#8B95A5] text-[10px] mb-1">{t('crm.highPriority')}</div>
        <div className="text-red-400 text-lg font-bold font-mono">{high_priority_count || 0}</div>
      </div>
    </div>
  );
};

const RegionalObligationsSection = ({ orgId, isAdmin }) => {
  const { t } = useTranslation('platform');
  const [obligations, setObligations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchObligations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      if (statusFilter !== "all") params.set("applicability_status", statusFilter);
      const [oblRes, sumRes] = await Promise.all([
        fetch(`${API}/crm/organizations/${orgId}/obligations?${params}`, { credentials: "include" }),
        fetch(`${API}/crm/organizations/${orgId}/obligations/summary`, { credentials: "include" }),
      ]);
      if (oblRes.ok) setObligations(await oblRes.json());
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (err) {
      console.error("Failed to load obligations:", err);
    }
    setLoading(false);
  }, [orgId, categoryFilter, statusFilter]);

  useEffect(() => { fetchObligations(); }, [fetchObligations]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/crm/organizations/${orgId}/obligations/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Obligations refreshed: ${data.refreshed_count} activations`);
        fetchObligations();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to refresh obligations");
      }
    } catch (err) {
      toast.error("Failed to refresh obligations");
    }
    setRefreshing(false);
  };

  // Group obligations by region
  const grouped = {};
  obligations.forEach(a => {
    if (!grouped[a.region]) grouped[a.region] = [];
    grouped[a.region].push(a);
  });

  return (
    <div data-testid="crm-obligations-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-semibold text-sm flex items-center gap-2">
          <Globe size={14} className="text-[#36A9E1]" /> {t('crm.obligations')}
          {summary && <span className="text-[#8B95A5] font-normal text-xs">({summary.total_obligations})</span>}
        </h2>
        {isAdmin && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-[#36A9E1] bg-[rgba(54,169,225,0.08)] hover:bg-[rgba(54,169,225,0.15)] disabled:opacity-50 transition-colors"
            data-testid="crm-obligations-refresh-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#36A9E1]" size={20} /></div>
      ) : obligations.length === 0 && (!summary || summary.total_obligations === 0) ? (
        <div className="text-center py-6 text-[#8B95A5] text-xs">
          <Globe size={24} className="mx-auto mb-2 opacity-30" />
          <p>{t('crm.noObligations')}</p>
        </div>
      ) : (
        <>
          <ObligationsSummaryCard summary={summary} />

          {/* Filters */}
          <div className="flex gap-2 mb-3">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="px-2 py-1.5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs"
              data-testid="crm-obligations-category-filter"
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-2 py-1.5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs"
              data-testid="crm-obligations-status-filter"
            >
              <option value="all">{t('crm.allStatuses')}</option>
              <option value="active">Active</option>
              <option value="under_review">Under Review</option>
              <option value="suppressed">Suppressed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Grouped by region */}
          <div className="space-y-3" data-testid="crm-obligations-list">
            {Object.entries(grouped).map(([region, items]) => (
              <div key={region} className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
                <div className="px-3 py-2 border-b border-[rgba(255,255,255,0.06)] flex items-center gap-2">
                  <MapPin size={12} className="text-[#36A9E1]" />
                  <span className="text-white text-xs font-semibold">{region}</span>
                  <span className="text-[#8B95A5] text-[10px]">{REGION_LABELS[region] || region}</span>
                  <span className="text-[#8B95A5] text-[10px] ml-auto">{items.length} obligations</span>
                </div>
                <div>
                  {items.map(a => <ObligationRow key={a.activation_id} activation={a} />)}
                </div>
              </div>
            ))}
          </div>

          {/* Region breakdown from summary */}
          {summary?.by_category && summary.by_category.length > 0 && (
            <div className="mt-4 rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-3" data-testid="crm-obligations-category-breakdown">
              <h3 className="text-[#8B95A5] text-[10px] font-medium mb-2 uppercase tracking-wider">Category Breakdown</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {summary.by_category.map(c => {
                  const catCfg = CATEGORY_CONFIG[c.category] || {};
                  return (
                    <div key={c.category} className="flex items-center gap-2 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${catCfg.bg || "bg-gray-500/15"}`} />
                      <span className="text-[#B8C7D9] flex-1">{catCfg.label || c.category}</span>
                      <span className="text-emerald-400 font-mono">{c.active}</span>
                      <span className="text-[#8B95A5]">/</span>
                      <span className="text-[#8B95A5] font-mono">{c.total}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============== MAIN PAGE ==============

const CRMOrganizationDetailPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { guidance } = useGuidance();
  const { t } = useTranslation('platform');
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddContact, setShowAddContact] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", role_title: "", is_primary: false });

  const fetchOrg = useCallback(async () => {
    try {
      const res = await fetch(`${API}/crm/organizations/${orgId}`, { credentials: "include" });
      if (res.ok) {
        setOrg(await res.json());
      } else {
        toast.error("Organization not found");
        navigate("/crm/organizations");
      }
    } catch (err) {
      toast.error("Failed to load organization");
    }
    setLoading(false);
  }, [orgId, navigate]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await fetch(`${API}/crm/organizations/${orgId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setEditStatus(null);
        fetchOrg();
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim()) return;
    setAddingContact(true);
    try {
      const res = await fetch(`${API}/crm/organizations/${orgId}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(contactForm),
      });
      if (res.ok) {
        toast.success("Contact added");
        setShowAddContact(false);
        setContactForm({ name: "", email: "", phone: "", role_title: "", is_primary: false });
        fetchOrg();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to add contact");
      }
    } catch (err) {
      toast.error("Failed to add contact");
    }
    setAddingContact(false);
  };

  if (loading) return <QMSLayout title="Organization"><div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div></QMSLayout>;
  if (!org) return null;

  const st = STATUS_STYLES[org.status] || STATUS_STYLES.inactive;
  const risk = RISK_STYLES[org.risk_category] || RISK_STYLES.medium;
  const healthColor = org.health_score >= 70 ? "#10B981" : org.health_score >= 50 ? "#EAB308" : "#EF4444";
  const contacts = org.contacts || [];
  const onboardingRuns = org.onboarding_runs || [];

  return (
    <QMSLayout title={org.name}>
      <div className="max-w-5xl mx-auto" data-testid="crm-org-detail-page">
        {/* Back button */}
        <button onClick={() => navigate("/crm/organizations")} className="flex items-center gap-1.5 text-[#8B95A5] hover:text-white text-sm mb-4 transition-colors" data-testid="crm-back-btn">
          <ArrowLeft size={14} /> {t('crm.backToOrgs')}
        </button>

        {/* Header */}
        <div className="p-5 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[rgba(54,169,225,0.1)] flex items-center justify-center">
                <Building2 size={22} className="text-[#36A9E1]" />
              </div>
              <div>
                <h1 className="text-white font-bold text-xl font-['Syne']" data-testid="crm-org-name">{org.name}</h1>
                <div className="flex items-center gap-3 mt-1 text-xs text-[#8B95A5]">
                  <span className="capitalize">{org.type}</span>
                  {org.industry && <span>{org.industry}</span>}
                  <span className="font-mono text-[10px]">{org.org_id}</span>
                </div>
              </div>
            </div>

            {/* Status + Health */}
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold font-mono" style={{ color: healthColor }} data-testid="crm-health-score">{org.health_score}</div>
                <div className="text-[#8B95A5] text-[10px]">Health</div>
              </div>
              {editStatus ? (
                <div className="flex items-center gap-1">
                  {["active", "onboarding", "inactive", "suspended"].map(s => (
                    <button key={s} onClick={() => handleStatusChange(s)} className={`px-2 py-1 rounded text-[10px] font-medium ${STATUS_STYLES[s].bg} ${STATUS_STYLES[s].text}`}>{STATUS_STYLES[s].label}</button>
                  ))}
                  <button onClick={() => setEditStatus(null)} className="p-1 text-[#8B95A5] hover:text-white"><X size={12} /></button>
                </div>
              ) : (
                <button onClick={() => setEditStatus(true)} className={`px-3 py-1.5 rounded text-xs font-medium ${st.bg} ${st.text} hover:opacity-80 transition-opacity`} data-testid="crm-org-status">
                  {st.label}
                </button>
              )}
            </div>
          </div>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
            {org.regions?.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-[#8B95A5]">
                <MapPin size={12} />
                <span>{org.regions.join(", ")}</span>
              </div>
            )}
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${risk.bg} ${risk.text}`}>
              <Shield size={12} />
              <span className="capitalize">{org.risk_category} risk</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#8B95A5]">
              <Users size={12} />
              <span>{contacts.length} contacts</span>
            </div>
            {org.assigned_owner_id && (
              <div className="flex items-center gap-1.5 text-xs text-[#8B95A5]">
                <User size={12} />
                <span>Owner: {org.assigned_owner_id}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column: Contacts + Obligations */}
          <div className="lg:col-span-2 space-y-4">
            {/* Contacts Panel */}
            <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27]">
              <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.06)]">
                <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Users size={14} className="text-[#A78BFA]" /> Contacts ({contacts.length})
                </h2>
                <button onClick={() => setShowAddContact(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-[#36A9E1] bg-[rgba(54,169,225,0.08)] hover:bg-[rgba(54,169,225,0.15)] transition-colors" data-testid="crm-add-contact-btn">
                  <Plus size={12} /> Add
                </button>
              </div>
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-[#8B95A5] text-sm">{t('crm.noContacts')}</div>
              ) : (
                <div className="divide-y divide-[rgba(255,255,255,0.04)]" data-testid="crm-contacts-list">
                  {contacts.map(c => (
                    <div key={c.contact_id} className="p-4 flex items-center gap-3" data-testid={`crm-contact-${c.contact_id}`}>
                      <div className="w-8 h-8 rounded-full bg-[rgba(167,139,250,0.1)] flex items-center justify-center">
                        <User size={14} className="text-[#A78BFA]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium truncate">{c.name}</span>
                          {c.is_primary && <Star size={10} className="text-[#EAB308] fill-[#EAB308]" />}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#8B95A5] mt-0.5">
                          {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                          {c.role_title && <span className="flex items-center gap-1"><Briefcase size={10} />{c.role_title}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Regional Obligations Panel — M8-P2 */}
            <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] p-4">
              <RegionalObligationsSection orgId={orgId} isAdmin={isAdmin} />
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Onboarding Linkage */}
            <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] p-4" data-testid="crm-onboarding-linkage">
              <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3">
                <ClipboardCheck size={14} className="text-[#36A9E1]" /> Onboarding Runs
              </h3>
              {onboardingRuns.length === 0 ? (
                <p className="text-[#8B95A5] text-xs">No onboarding runs linked</p>
              ) : (
                <div className="space-y-2">
                  {onboardingRuns.map(run => (
                    <div key={run.run_id} className="p-2 rounded bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)]">
                      <div className="text-white text-xs font-medium">{run.run_id}</div>
                      <div className="text-[#8B95A5] text-[10px] mt-0.5 capitalize">{(run.status || "unknown").replace("_", " ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Health Breakdown */}
            <div className="rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] p-4" data-testid="crm-health-breakdown">
              <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3">
                <Activity size={14} style={{ color: healthColor }} /> {t('crm.healthScore')}
              </h3>
              <div className="text-center mb-3">
                <div className="text-3xl font-bold font-mono" style={{ color: healthColor }}>{org.health_score}</div>
                <div className="text-[#8B95A5] text-xs mt-1">
                  {org.health_score >= 70 ? t('crm.healthy') : org.health_score >= 50 ? t('crm.needsAttention') : t('crm.atRisk')}
                </div>
              </div>
              <div className="w-full h-2 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${org.health_score}%`, backgroundColor: healthColor }} />
              </div>
            </div>
          </div>
        </div>

        {guidance?.support_options && <SupportChoiceBar options={guidance.support_options} />}

        {/* Add Contact Modal */}
        {showAddContact && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="crm-add-contact-modal">
            <form onSubmit={handleAddContact} className="bg-[#1A1D27] border border-[rgba(255,255,255,0.1)] rounded-lg p-6 w-full max-w-md shadow-2xl">
              <h2 className="text-white font-semibold text-lg mb-4">{t('crm.addContact')}</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">Name *</label>
                  <input value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none" required data-testid="crm-contact-form-name" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">Email *</label>
                  <input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none" required data-testid="crm-contact-form-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">Phone</label>
                    <input value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm" data-testid="crm-contact-form-phone" />
                  </div>
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">Role / Title</label>
                    <input value={contactForm.role_title} onChange={e => setContactForm(p => ({ ...p, role_title: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm" data-testid="crm-contact-form-role" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={contactForm.is_primary} onChange={e => setContactForm(p => ({ ...p, is_primary: e.target.checked }))} className="rounded" data-testid="crm-contact-form-primary" />
                  <span className="text-[#8B95A5] text-sm">Primary contact</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setShowAddContact(false)} className="px-4 py-2 rounded-md text-sm text-[#8B95A5] hover:text-white transition-colors">Cancel</button>
                <button type="submit" disabled={addingContact} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] disabled:opacity-50 transition-colors" data-testid="crm-contact-form-submit">
                  {addingContact ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default CRMOrganizationDetailPage;
